import React from 'react';
import { useTranslation } from 'react-i18next';

import Modal from 'modules/views/shared/Modal';

import {
  validateCertificationGroups,
  getEffectiveDateText,
  shouldShowDateText,
  CertificationGroup,
  CertificationItem,
} from '../utils/certificationValidation';

import { useStylesCertificationWarningModal } from '../styles';

interface ICertificationWarningModalProps {
  isVisible: boolean;
  certificationGroups: CertificationGroup[];
  allCertifications: CertificationItem[];
  onClose: () => void;
  onCancel: () => void;
  onProceedAnyway: () => void;
  isViewMoreMode?: boolean;
  onAddCertificate?: () => void;
}

const CertificationWarningModal = ({
  isVisible,
  certificationGroups,
  allCertifications,
  onClose,
  onCancel,
  onProceedAnyway,
  isViewMoreMode = false,
  onAddCertificate,
}: ICertificationWarningModalProps) => {
  const classes = useStylesCertificationWarningModal();
  const { t } = useTranslation();

  // Get the groups that require warnings
  const requiredGroups = validateCertificationGroups(certificationGroups, allCertifications);

  return (
    <Modal
      title={isViewMoreMode ? t('certifications.warning.requiredCertifications') : t('certifications.warning.title')}
      isVisible={isVisible}
      modalStyleClass={classes.modal}
      Content={
        <div className={classes.content}>
          {requiredGroups.map((group, groupIndex) => (
            <div key={group.id} className={classes.section}>
              <p className={classes.sectionTitle}>
                {t('certifications.warning.pleaseAdd')}{' '}
                {group.validationType === 3
                  ? t('certifications.warning.allThe')
                  : t('certifications.warning.atLeastOneOfThe')}{' '}
                "{group.name}"{t('certifications.warning.certifications')}
                {shouldShowDateText(group) ? ` (${getEffectiveDateText(group, t)})` : ''}
              </p>
              <ul className={classes.certificationList}>
                {group.certifications.map((certification, index) => (
                  <li key={certification.id} className={classes.listItem}>
                    {certification.alias || certification.name}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <p className={classes.warningText}>{t('certifications.warning.consequenceText')}</p>
        </div>
      }
      actionButtons={[
        {
          label: isViewMoreMode ? t('common.close') : t('certifications.warning.cancel'),
          styleClass: classes.cancelButton,
          variant: 'outlined',
          onClick: onCancel,
          testId: 'cert-warning-cancel-button',
        },
        isViewMoreMode
          ? {
              label: t('certifications.addNewCertificate'),
              styleClass: classes.addCertificationButton,
              onClick: onAddCertificate,
              testId: 'cert-warning-add-button',
            }
          : {
              label: t('certifications.warning.proceedAnyway'),
              styleClass: classes.proceedButton,
              onClick: onProceedAnyway,
              testId: 'cert-warning-proceed-button',
            },
      ]}
      onClose={onClose}
    />
  );
};

export default React.memo(CertificationWarningModal);
